<script setup lang="ts">
  import { PropType, computed, ref, watch, onMounted, onUnmounted, provide, defineExpose, nextTick } from 'vue'
  import { Editor, EditorState } from '@tiptap/vue-3'
  import {
    Color,
    LineHeight,
    Link,
    RequiredExtensions,
    TextAlign,
    TextBlocks,
    TextSize,
    UIRichTextEditor,
  } from '@gohighlevel/ghl-text-editor'
  import TextStyle from '@tiptap/extension-text-style'
  import Underline from '@tiptap/extension-underline'
  import BulletList from '@tiptap/extension-bullet-list'
  import StarterKit from '@tiptap/starter-kit'
  import ListItem from '@tiptap/extension-list-item'
  import HardBreak from '@tiptap/extension-hard-break'
  import {
    MagicPlaceholderExtension,
    ELEMENTS_META,
    ExtendedFontFamily,
    IElement,
    Storage,
    CustomLineHeight,
    extractFontFamiliesFromJSON,
    FontFamilyV2,
    ExtendedHeading,
    InsertGapOnPaste,
  } from '@gohighlevel/ghl-proposals-common'
  import { useElementOption } from '@/composable/elementOptions'
  import { useDocument } from '@/composition'
  import { useBuilderStore } from '@/store/builder'

  const editor = ref<Editor>()
  const store = useBuilderStore()
  const { updateOption } = useElementOption()
  const { loadingCustomValues, documentVersion } = useDocument()

  // Add a ref to store link state
  const currentLinkState = ref<any>(null)

  // Provide the link state to child components
  provide('currentLinkState', currentLinkState)

  // Add a function to update link state
  const updateLinkState = () => {
    if (editor.value) {
      const attrs = editor.value.getAttributes('link')
      if (attrs?.href) {
        currentLinkState.value = attrs
        // Provide the link state to child components
        provide('rte-link-state', attrs)
      }
    }
  }

  const props = defineProps({
    genKey: { type: String },
    element: {
      type: Object as PropType<IElement>,
    },
    customValues: {
      type: Object as PropType<Record<string, string>>,
      required: true,
    },
  })
  const isEditable = computed(() => store.isDocumentEditable)

  const CustomBulletList = BulletList.extend({
    addCommands() {
      return {
        ...this.parent?.(),
        toggleCustomBulletList: () => editor => {
          return editor.commands.toggleBulletList()
        },
      }
    },
  })

  const HardBreakExtension = HardBreak.extend({
    addKeyboardShortcuts() {
      return {
        Enter: () => {
          if (this.editor.isActive('orderedList') || this.editor.isActive('bulletList')) {
            return this.editor.chain().createParagraphNear().run()
          }
          return this.editor.commands.setHardBreak()
        },
      }
    },
  })

  editor.value = new Editor({
    content: props.element?.component.options.text,
    extensions: [
      StarterKit.configure({
        paragraph: {
          HTMLAttributes: {
            class: 'break-inside-auto',
          },
        },
      }),
      MagicPlaceholderExtension.configure({
        placeholderValues: props.customValues,
        loading: loadingCustomValues.value,
      }),
      TextSize,
      RequiredExtensions,
      TextStyle,
      ...((documentVersion.value ?? 1) === 1 ? [ExtendedFontFamily] : []),
      ...((documentVersion.value ?? 1) > 1 ? [FontFamilyV2] : []),
      TextBlocks,
      ...((documentVersion.value ?? 1) > 1 ? [ExtendedHeading] : []), // don't change the order, this should be added after TextBlocks
      Color,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        protocols: ['ftp', 'mailto', '#popup', 'sms', 'tel', '{{', '/', 'http', 'https'],
        autolink: true,
        linkOnPaste: false,
      }),
      // Replace the LineHeight extension with CustomLineHeight
      ...((documentVersion.value ?? 1) > 1
        ? [
            CustomLineHeight.configure({
              defaultLineHeight: 1.5,
              types: ['paragraph', 'heading', 'blockquote', 'listItem'],
            }),
          ]
        : []),
      ...((documentVersion.value ?? 1) === 1 ? [LineHeight] : []),
      ListItem,
      CustomBulletList,
      ...((documentVersion.value ?? 1) === 1
        ? [
            HardBreakExtension.configure({
              HTMLAttributes: {
                class: 'break-after-auto page-break',
              },
            }),
          ]
        : []),
      Storage.configure({
        storage: {
          version: props.element?.version ?? 1,
          documentVersion: documentVersion.value ?? 1,
        },
      }),
      ...((documentVersion.value ?? 1) > 1 ? [InsertGapOnPaste] : []),
    ],
    autofocus: false,
    editable: isEditable.value,
    onUpdate({ editor }) {
      updateOption(ELEMENTS_META.TEXT, editor.getHTML(), props?.element?.id, props?.element?.type)
    },
    editorProps: {
      handleClick: () => {
        updateLinkState()
        return false // Let other click handlers run
      },
      attributes: (state: EditorState) => {
        return {
          ...state.doc.attrs,
          ...(documentVersion.value === 1 ? { style: 'font-family: sans-serif' } : {}),
          ...(documentVersion.value > 1 ? { style: 'font-family: Open Sans' } : {}),
        }
      },
    },
    parseOptions: {
      preserveWhitespace: true,
    },
  })

  function onRichTextEditorFocus() {
    store.updateEditorInstance(editor.value as unknown as typeof Editor)
    updateLinkState()
  }

  function onRichTextEditorSelectionUpdate() {
    updateLinkState()
  }

  function onRichTextEditorBlur() {
    if (documentVersion.value > 1) {
      const fonts = extractFontFamiliesFromJSON(editor.value?.getJSON())
      store.setLoadedFontsFromEditor(Array.from(fonts))
    }
    if (props.element?.id) {
      store.flushPendingSectionPropagations(props.element.id)
    }
  }

  function onRichTextEditorCreate() {
    if (documentVersion.value > 1) {
      const fonts = extractFontFamiliesFromJSON(editor.value?.getJSON())
      store.setLoadedFontsFromEditor(Array.from(fonts))
    }
    if (store.activeElementId === props.element?.id) {
      nextTick(() => {
        store.updateEditorInstance(editor.value as unknown as typeof Editor)
      })
    }
  }

  // Export currentLinkState for use in the modal
  defineExpose({
    currentLinkState,
  })

  watch(isEditable, () => {
    editor.value?.setEditable(isEditable.value)
  })

  // Only for undo/redo operation to work since tiptap editor does not refresh on updating pages state.
  // Uses `flush: 'post'` so the callback runs AFTER Vue has patched the DOM and `props.element`
  // reflects the latest cloned element. With the default `flush: 'pre'`, the watcher ran BEFORE the
  // parent re-rendered, so `props.element.component.options.text` was still the PREVIOUS clone's
  // content — producing a one-step lag where other pages always showed the last-but-one edit.
  const previousUpdateEditors = ref(store.updateEditors)
  watch(
    () => store.updateEditors,
    newValue => {
      if (newValue !== previousUpdateEditors.value && props.element?.component?.options?.text !== undefined) {
        const currentContent = editor.value?.getHTML()
        const newContent = props.element.component.options.text
        if (currentContent !== newContent) {
          editor.value?.commands.setContent(newContent)
        }
      }
      previousUpdateEditors.value = newValue
    },
    { deep: true, flush: 'post' }
  )

  watch(
    () => store.moveElementDragState,
    val => {
      editor.value?.setEditable(!val)
    }
  )

  // Helper function to check if an element is at least partially visible in the viewport
  const isElementInViewport = (element: HTMLElement): boolean => {
    const rect = element.getBoundingClientRect()
    const windowHeight = window.innerHeight || document.documentElement.clientHeight
    const windowWidth = window.innerWidth || document.documentElement.clientWidth
    // Check if element intersects with viewport (at least partially visible)
    return rect.bottom > 0 && rect.right > 0 && rect.top < windowHeight && rect.left < windowWidth
  }

  // Watch for when this element becomes active and set the editor instance
  // This ensures the editor instance is set when clicking on a text block
  watch(
    () => store.activeElementId,
    newActiveElementId => {
      // If this element becomes active and editor exists, set the editor instance directly
      // This ensures the toolbar (font, line height, etc.) is shown immediately
      if (newActiveElementId === props.element?.id && editor.value) {
        // Use nextTick to ensure the DOM is updated and editor is ready
        nextTick(() => {
          // Directly set the editor instance in the store
          // This ensures the toolbar shows even if focus doesn't fire immediately
          store.updateEditorInstance(editor.value as unknown as typeof Editor)
          // Only focus if the editor is already in the viewport to prevent scrolling
          // This prevents scrolling to the last page when double-clicking on a text block
          setTimeout(() => {
            const editorDOM = editor.value?.view?.dom as HTMLElement
            if (editorDOM && isElementInViewport(editorDOM)) {
              editor.value?.commands.focus()
            }
          }, 10)
        })
      }
    },
    { immediate: false }
  )

  // Add watcher for link state changes
  watch(currentLinkState, newState => {
    if (newState?.href) {
      // Emit an event that the modal component can listen to
      editor.value?.emit('link-state-update', newState)
    }
  })

  onMounted(() => {
    const ed = editor.value
    if (!ed) return
    ed.on('focus', onRichTextEditorFocus)
    ed.on('selectionUpdate', onRichTextEditorSelectionUpdate)
    ed.on('blur', onRichTextEditorBlur)
    ed.on('create', onRichTextEditorCreate)
    onUnmounted(() => {
      ed.off('focus', onRichTextEditorFocus)
      ed.off('selectionUpdate', onRichTextEditorSelectionUpdate)
      ed.off('blur', onRichTextEditorBlur)
      ed.off('create', onRichTextEditorCreate)
      ed.destroy()
    })
  })
</script>

<template>
  <UIRichTextEditor :editor="editor" />
</template>
